import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { adminFetch } from '@/lib/adminApi';

// Use static URL paths for fallback images instead of bundled imports.
// This removes ~6 large images from the initial JS chunk, dramatically
// improving FCP and Speed Index. The images are still in src/assets and
// processed by vite, but we reference them lazily.
const redbullTower = new URL('@/assets/projects/redbull-tower.jpg', import.meta.url).href;
const aperolTrailer = new URL('@/assets/projects/aperol-trailer.jpg', import.meta.url).href;
const bentleyBar = new URL('@/assets/projects/bentley-bar.jpg', import.meta.url).href;
const campariSchaufenster = new URL('@/assets/projects/campari-schaufenster.jpg', import.meta.url).href;
const suzePaleo = new URL('@/assets/projects/suze-paleo.jpg', import.meta.url).href;
const diversePosm = new URL('@/assets/projects/diverse-posm.jpg', import.meta.url).href;
const werbetechnikFahrzeug = new URL('@/assets/projects/werbetechnik-fahrzeug.jpg', import.meta.url).href;
const werbetechnikSchaufenster = new URL('@/assets/projects/werbetechnik-schaufenster.jpg', import.meta.url).href;
const werbetechnikGrossformat = new URL('@/assets/projects/werbetechnik-grossformat.jpg', import.meta.url).href;

export type ProjectCategory = 'Eventbau & Logistik' | 'Aktivierung & Promotion' | 'Werbetechnik' | 'Promotional Items';

export const PROJECT_CATEGORIES: ProjectCategory[] = [
  'Eventbau & Logistik',
  'Aktivierung & Promotion',
  'Werbetechnik',
  'Promotional Items'
];

export type OverlayPosition = 'left' | 'right';

export interface ClientQuote {
  quote_de?: string | null;
  quote_en?: string | null;
  name?: string | null;
  role?: string | null;
}

export interface OutcomeMetric {
  label_de?: string | null;
  label_en?: string | null;
  value: string;
}

export interface Project {
  id: string;
  title: string;
  titleEn?: string | null;
  descriptionPreview: string;
  descriptionPreviewEn?: string | null;
  descriptionFull: string;
  descriptionFullEn?: string | null;
  bullets: string[];
  instagramLink: string;
  videoUrl?: string | null;
  images: string[];
  status: 'published' | 'draft';
  date: string;
  categories: ProjectCategory[];
  tags: string[];
  tagsEn?: string[] | null;
  overlayPosition: OverlayPosition;
  displayOrder: number;
  // Case-study scaffold fields (all optional; render only when present)
  challenge?: string | null;
  challengeEn?: string | null;
  approach?: string | null;
  approachEn?: string | null;
  results?: string | null;
  resultsEn?: string | null;
  clientQuote?: ClientQuote | null;
  outcomeMetrics?: OutcomeMetric[] | null;
}

// Initial projects for seeding
const INITIAL_PROJECTS: Project[] = [
  {
    id: '1',
    title: 'Red Bull Energy Tower',
    descriptionPreview: 'Ein monumentaler 12-Meter-Turm für das ultimative Festival-Erlebnis.',
    descriptionFull: 'Ein monumentaler 12-Meter-Turm für das ultimative Festival-Erlebnis. Maßgeschneiderte Stahlkonstruktion mit integriertem Branding für die Red Bull Energy Refuel Stationen.\n\nDieses Projekt vereint technische Präzision mit kreativem Design. Von der Konzeption bis zur finalen Umsetzung haben wir jeden Schritt begleitet – inklusive statischer Berechnungen, Materialbeschaffung und On-Site-Montage.',
    bullets: ['Stahlkonstruktion', 'Festival-Installation', 'Branding Integration'],
    instagramLink: 'https://instagram.com/go4it',
    images: [redbullTower],
    status: 'published',
    date: '2025-08-15',
    categories: ['Eventbau & Logistik'],
    tags: ['Stage Design', 'Logistik', 'Installation', 'Branding'],
    overlayPosition: 'left',
    displayOrder: 1
  },
  {
    id: '2',
    title: 'Suze Bar Activation @ Paleo',
    descriptionPreview: 'Kompletter Aufbau und logistische Meisterleistung für die Suze Bar am Paleo Festival Nyon.',
    descriptionFull: 'Kompletter Aufbau und logistische Meisterleistung für die Suze Bar am Paleo Festival Nyon. Von der Bodenplatte bis zur Dachkonstruktion.\n\nDas Paleo Festival ist eines der grössten Open-Air-Festivals der Schweiz. Für Suze haben wir eine vollständig gebrandete Bar-Einheit konzipiert und vor Ort aufgebaut – unter Berücksichtigung aller Sicherheitsvorschriften und zeitlichen Vorgaben.',
    bullets: ['Festival Bau', 'Bar Design', 'Logistik'],
    instagramLink: 'https://instagram.com/go4it',
    images: [suzePaleo],
    status: 'published',
    date: '2025-07-25',
    categories: ['Eventbau & Logistik'],
    tags: ['Festival Bau', 'Bar Design', 'Logistik', 'Atmosphere'],
    overlayPosition: 'right',
    displayOrder: 2
  },
  {
    id: '3',
    title: 'Aperol Event Trailer',
    descriptionPreview: "Mobile Bar-Lösung mit italienischem Flair unter dem Motto 'Casa Fondata Padova Italia'.",
    descriptionFull: "Mobile Bar-Lösung mit italienischem Flair. Unter dem Motto 'Casa Fondata Padova Italia' bringen wir den perfekten Aperol Spritz direkt zu den Leuten.\n\nDer voll ausgestattete Event-Trailer ist das Herzstück jeder Roadshow. Mit integrierter Kühlung, Barausstattung und Branding-Elementen ist er sofort einsatzbereit – ob am Stadtfest, Firmen-Event oder am See.",
    bullets: ['Mobile Bar', 'Roadshow', 'Sampling'],
    instagramLink: 'https://instagram.com/go4it',
    images: [aperolTrailer],
    status: 'published',
    date: '2025-07-20',
    categories: ['Aktivierung & Promotion'],
    tags: ['Roadshow', 'Mobile Bar', 'Sampling', 'Staffing'],
    overlayPosition: 'left',
    displayOrder: 3
  },
  {
    id: '4',
    title: 'Bentley Bar Activation',
    descriptionPreview: 'Luxus trifft auf Genuss. Eine High-End Bar-Installation für Bentley.',
    descriptionFull: 'Luxus trifft auf Genuss. Eine High-End Bar-Installation für die exklusivste Automarke der Welt mit edlen Materialien und perfektem Service.\n\nFür diesen exklusiven Kundenanlass haben wir eine temporäre Bar konzipiert, die den Ansprüchen der Marke Bentley gerecht wird. Hochwertige Materialien, dezente Beleuchtung und erstklassiger Service bilden das Fundament dieser Aktivierung.',
    bullets: ['Premium Event', 'Hospitality', 'Luxury Design'],
    instagramLink: 'https://instagram.com/go4it',
    images: [bentleyBar],
    status: 'published',
    date: '2025-06-10',
    categories: ['Aktivierung & Promotion'],
    tags: ['Premium', 'Event', 'Hospitality', 'Design'],
    overlayPosition: 'right',
    displayOrder: 4
  },
  {
    id: '5',
    title: 'DOTW x Campari Schaufenster',
    descriptionPreview: 'Visual Merchandising auf neuem Level. Ein Schaufenster, das nicht nur zeigt, sondern erzählt.',
    descriptionFull: 'Visual Merchandising auf neuem Level. Ein Schaufenster, das nicht nur zeigt, sondern erzählt. Exklusive Umsetzung für Drinks of the World.\n\nDie Schaufenster-Installation für DOTW und Campari kombiniert klassische Eleganz mit modernem Storytelling. Jedes Element wurde sorgfältig ausgewählt, um die Markenidentität zu unterstreichen und Passanten in den Bann zu ziehen.',
    bullets: ['Visual Merchandising', 'Display Design', 'Print Production'],
    instagramLink: 'https://instagram.com/go4it',
    images: [campariSchaufenster],
    status: 'published',
    date: '2025-05-05',
    categories: ['Promotional Items'],
    tags: ['Visual Merchandising', 'Display', 'Print', 'Installation'],
    overlayPosition: 'left',
    displayOrder: 5
  },
  {
    id: '6',
    title: 'Diverse POSM Lösungen',
    descriptionPreview: 'Massgeschneiderte Werbemittel für Premium-Marken wie Jameson, Macallan und Lallier.',
    descriptionFull: 'Massgeschneiderte Werbemittel für Premium-Marken wie Jameson, Macallan, Lallier und Migros Bank. Innovative Displays für den Point of Sale.\n\nVon der ersten Skizze bis zur Auslieferung übernehmen wir die komplette Produktion von POS-Materialien. Unsere Lösungen sind funktional, ästhetisch ansprechend und perfekt auf die jeweilige Marke abgestimmt.',
    bullets: ['Production', 'Design', 'Logistik'],
    instagramLink: 'https://instagram.com/go4it',
    images: [diversePosm],
    status: 'published',
    date: '2025-04-15',
    categories: ['Promotional Items'],
    tags: ['Production', 'Design', 'Logistik', 'Procurement'],
    overlayPosition: 'right',
    displayOrder: 6
  },
  {
    id: '7',
    title: 'Fahrzeugfolierung Premium Fleet',
    descriptionPreview: 'Komplette Fahrzeugfolierung und Fleet-Branding für maximale mobile Markenpräsenz auf Schweizer Strassen.',
    descriptionFull: 'Komplette Fahrzeugfolierung und Fleet-Branding für maximale mobile Markenpräsenz auf Schweizer Strassen.\n\nVon der Designentwicklung über die Produktion bis zur fachgerechten Montage – wir verwandeln Fahrzeugflotten in rollende Werbeflächen. Hochwertige Folien garantieren Langlebigkeit und brillante Farben.',
    bullets: ['Fahrzeugfolierung', 'Fleet Branding', 'Montage'],
    instagramLink: 'https://instagram.com/go4it',
    images: [werbetechnikFahrzeug],
    status: 'published',
    date: '2025-03-20',
    categories: ['Werbetechnik'],
    tags: ['Folierung', 'Fahrzeug', 'Branding', 'Montage'],
    overlayPosition: 'left',
    displayOrder: 7
  },
  {
    id: '8',
    title: 'Schaufensterbeschriftung Luxury Retail',
    descriptionPreview: 'Elegante Schaufensterbeschriftungen mit Goldfolie und Vinyllettering für den Luxus-Einzelhandel in Zürich.',
    descriptionFull: 'Elegante Schaufensterbeschriftungen mit Goldfolie und Vinyllettering für den Luxus-Einzelhandel in Zürich.\n\nJedes Detail zählt – von der Schriftauswahl bis zur präzisen Applikation. Unsere Schaufensterbeschriftungen verbinden Handwerkskunst mit modernem Design und schaffen einen bleibenden Eindruck bei jedem Passanten.',
    bullets: ['Vinyllettering', 'Goldfolie', 'Schaufenster'],
    instagramLink: 'https://instagram.com/go4it',
    images: [werbetechnikSchaufenster],
    status: 'published',
    date: '2025-02-15',
    categories: ['Werbetechnik'],
    tags: ['Beschriftung', 'Schaufenster', 'Folie', 'Design'],
    overlayPosition: 'right',
    displayOrder: 8
  },
  {
    id: '9',
    title: 'Grossformatdruck Fassadenbanner',
    descriptionPreview: 'Grossformatige Fassadenbanner und Baustellenverkleidungen – vom Druck bis zur Montage in der ganzen Schweiz.',
    descriptionFull: 'Grossformatige Fassadenbanner und Baustellenverkleidungen – vom Druck bis zur Montage in der ganzen Schweiz.\n\nOb Bauprojekt, Messe oder Sonderaktion: Unsere Grossformatdrucke setzen Marken grossflächig in Szene. Wetterfeste Materialien und professionelle Installation garantieren höchste Qualität und Sichtbarkeit.',
    bullets: ['Grossformatdruck', 'Fassadenbanner', 'Installation'],
    instagramLink: 'https://instagram.com/go4it',
    images: [werbetechnikGrossformat],
    status: 'published',
    date: '2025-01-10',
    categories: ['Werbetechnik'],
    tags: ['Grossformat', 'Banner', 'Druck', 'Installation'],
    overlayPosition: 'left',
    displayOrder: 9
  }
];

interface ProjectContextType {
  projects: Project[];
  loading: boolean;
  addProject: (project: Omit<Project, 'id'>) => Promise<void>;
  updateProject: (project: Project) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  updateProjectOrder: (projectId: string, newOrder: number) => Promise<void>;
  reorderProjects: (reorderedProjects: { id: string; displayOrder: number }[]) => Promise<void>;
  getPublishedProjects: () => Project[];
  getProjectsByCategory: (category: ProjectCategory) => Project[];
  refreshProjects: () => Promise<void>;
  uploadImage: (file: File, bucket: 'project-images' | 'team-images') => Promise<string>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

// Convert DB row to Project interface
const dbRowToProject = (row: any): Project => ({
  id: row.id,
  title: row.title,
  titleEn: row.title_en || null,
  descriptionPreview: row.short_description || '',
  descriptionPreviewEn: row.short_description_en || null,
  descriptionFull: row.description,
  descriptionFullEn: row.description_en || null,
  bullets: row.bullets || [],
  instagramLink: row.instagram_link || '',
  videoUrl: row.video_url || null,
  images: row.images || [],
  status: row.is_published ? 'published' : 'draft',
  date: row.created_at.split('T')[0],
  categories: (row.categories || []) as ProjectCategory[],
  tags: row.tags || [],
  tagsEn: row.tags_en || null,
  overlayPosition: (row.overlay_position || 'left') as OverlayPosition,
  displayOrder: row.display_order || 0,
  challenge: row.challenge || null,
  challengeEn: row.challenge_en || null,
  approach: row.approach || null,
  approachEn: row.approach_en || null,
  results: row.results || null,
  resultsEn: row.results_en || null,
  clientQuote: row.client_quote || null,
  outcomeMetrics: row.outcome_metrics || null,
});

// Convert Project to DB row format
const projectToDbRow = (project: Omit<Project, 'id'> | Project) => ({
  title: project.title,
  description: project.descriptionFull,
  short_description: project.descriptionPreview,
  banner_image: project.images[0] || '',
  categories: project.categories,
  bullets: project.bullets,
  tags: project.tags,
  images: project.images,
  instagram_link: project.instagramLink,
  title_en: (project as any).titleEn || null,
  short_description_en: (project as any).descriptionPreviewEn || null,
  description_en: (project as any).descriptionFullEn || null,
  tags_en: (project as any).tagsEn || null,
  challenge: (project as any).challenge || null,
  challenge_en: (project as any).challengeEn || null,
  approach: (project as any).approach || null,
  approach_en: (project as any).approachEn || null,
  results: (project as any).results || null,
  results_en: (project as any).resultsEn || null,
  client_quote: (project as any).clientQuote || null,
  outcome_metrics: (project as any).outcomeMetrics || null,
});

export const ProjectProvider = ({ children }: { children: ReactNode }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  // Helper to check if user is authenticated (cookie-based auth, no token needed)
  const isAuthenticated = () => sessionStorage.getItem('isAuthenticated') === 'true';

  const fetchProjects = async () => {
    try {
      // Public read - still uses direct Supabase query (SELECT allowed for published)
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching projects:', error);
        // Fallback to initial projects if DB fails
        setProjects(INITIAL_PROJECTS);
        return;
      }

      if (data && data.length > 0) {
        setProjects(data.map(dbRowToProject));
      } else {
        // If no projects in DB, seed with initial projects via Edge Function
        await seedInitialProjects();
      }
    } catch (err) {
      console.error('Error in fetchProjects:', err);
      setProjects(INITIAL_PROJECTS);
    } finally {
      setLoading(false);
    }
  };

  const seedInitialProjects = async () => {
    if (!isAuthenticated()) {
      // No admin session, just use initial projects without seeding
      setProjects(INITIAL_PROJECTS);
      return;
    }

    try {
      for (const p of INITIAL_PROJECTS) {
        const dbRow = projectToDbRow(p);
        const { error, data } = await adminFetch('admin-projects', {
          method: 'POST',
          body: dbRow,
        });

        if (error || (data as any)?.error) {
          throw new Error((data as any)?.error || error?.message || 'Failed to seed projects');
        }
      }
      
      // Refetch to get proper IDs
      const { data } = await supabase
        .from('projects')
        .select('*')
        .order('display_order', { ascending: true });

      if (data) {
        setProjects(data.map(dbRowToProject));
      }
    } catch (err) {
      console.error('Error seeding projects:', err);
      setProjects(INITIAL_PROJECTS);
    }
  };

  // Defer the initial fetch so it doesn't compete with FCP rendering
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = (window as any).requestIdleCallback(() => fetchProjects());
      return () => (window as any).cancelIdleCallback(id);
    } else {
      const timer = setTimeout(fetchProjects, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  const triggerTranslation = async (projectId: string, title: string, shortDescription: string, description: string, tags: string[]) => {
    try {
      console.log('Triggering translation for project:', projectId);
      const { data, error } = await supabase.functions.invoke('translate-project', {
        body: { projectId, title, shortDescription, description, tags }
      });
      
      if (error) {
        console.error('Translation error:', error);
        return;
      }
      
      console.log('Translation completed:', data);
      // Refresh projects to get the translations
      await fetchProjects();
    } catch (err) {
      console.error('Error triggering translation:', err);
    }
  };

  const addProject = async (project: Omit<Project, 'id'>) => {
    if (!isAuthenticated()) throw new Error('Admin session required');

    const dbRow = projectToDbRow(project);

    const { data: response, error } = await adminFetch<{ data?: any; error?: string }>('admin-projects', {
      method: 'POST',
      body: dbRow,
    });

    if (error || response?.error) {
      console.error('Error adding project:', error || response?.error);
      throw new Error(response?.error || error?.message || 'Failed to add project');
    }

    if (response?.data) {
      setProjects(prev => [dbRowToProject(response.data), ...prev]);
      // Trigger translation in background
      triggerTranslation(
        response.data.id,
        project.title,
        project.descriptionPreview,
        project.descriptionFull,
        project.tags
      );
    }
  };

  const updateProject = async (project: Project) => {
    if (!isAuthenticated()) throw new Error('Admin session required');

    const dbRow = projectToDbRow(project);

    const { data: response, error } = await adminFetch<{ data?: any; error?: string }>('admin-projects', {
      method: 'PUT',
      queryParams: { id: project.id },
      body: dbRow,
    });

    if (error || response?.error) {
      console.error('Error updating project:', error || response?.error);
      throw new Error(response?.error || error?.message || 'Failed to update project');
    }

    setProjects(prev => prev.map(p => p.id === project.id ? project : p));
    // Trigger translation in background
    triggerTranslation(project.id, project.title, project.descriptionPreview, project.descriptionFull, project.tags);
  };

  const deleteProject = async (id: string) => {
    if (!isAuthenticated()) throw new Error('Admin session required');

    const { data: response, error } = await adminFetch<{ success?: boolean; error?: string }>('admin-projects', {
      method: 'DELETE',
      queryParams: { id },
    });

    if (error || response?.error) {
      console.error('Error deleting project:', error || response?.error);
      throw new Error(response?.error || error?.message || 'Failed to delete project');
    }

    setProjects(prev => prev.filter(p => p.id !== id));
  };

  const updateProjectOrder = async (projectId: string, newOrder: number) => {
    if (!isAuthenticated()) throw new Error('Admin session required');

    const { data: response, error } = await adminFetch<{ data?: any; error?: string }>('admin-projects', {
      method: 'PUT',
      queryParams: { id: projectId },
      body: { display_order: newOrder },
    });

    if (error || response?.error) {
      console.error('Error updating project order:', error || response?.error);
      throw new Error(response?.error || error?.message || 'Failed to update order');
    }

    setProjects(prev => prev.map(p => 
      p.id === projectId ? { ...p, displayOrder: newOrder } : p
    ).sort((a, b) => a.displayOrder - b.displayOrder));
  };

  const reorderProjects = async (reorderedProjects: { id: string; displayOrder: number }[]) => {
    if (!isAuthenticated()) throw new Error('Admin session required');

    const { data: response, error } = await adminFetch<{ success?: boolean; error?: string }>('admin-projects', {
      method: 'PATCH',
      body: {
        reorders: reorderedProjects.map(p => ({ id: p.id, display_order: p.displayOrder })),
      },
    });

    if (error || response?.error) {
      console.error('Error reordering projects:', error || response?.error);
      throw new Error(response?.error || error?.message || 'Failed to reorder projects');
    }

    // Update local state
    setProjects(prev => {
      const updated = prev.map(p => {
        const newOrder = reorderedProjects.find(r => r.id === p.id);
        return newOrder ? { ...p, displayOrder: newOrder.displayOrder } : p;
      });
      return updated.sort((a, b) => a.displayOrder - b.displayOrder);
    });
  };

  const getPublishedProjects = () => {
    return projects.filter(p => p.status === 'published');
  };

  const getProjectsByCategory = (category: ProjectCategory) => {
    return projects.filter(p => p.status === 'published' && p.categories.includes(category));
  };

  const refreshProjects = async () => {
    setLoading(true);
    await fetchProjects();
  };

  const uploadImage = async (file: File, bucket: 'project-images' | 'team-images'): Promise<string> => {
    if (!isAuthenticated()) throw new Error('Admin session required');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucket', bucket);

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-image`;
    const sessionToken = sessionStorage.getItem('adminSessionToken');

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {})
      },
      credentials: 'include',
      body: formData
    });

    if (!res.ok) {
      const errData = await res.json();
      throw new Error(errData.error || 'Failed to upload image');
    }

    const data = await res.json();
    return data.publicUrl;
  };

  return (
    <ProjectContext.Provider value={{ 
      projects, 
      loading,
      addProject, 
      updateProject, 
      deleteProject,
      updateProjectOrder,
      reorderProjects,
      getPublishedProjects,
      getProjectsByCategory,
      refreshProjects,
      uploadImage
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};
